import React, { useCallback } from "react";
import { I18n, translate } from "@td/shared_utils";
import ParticipantsList from "./participants-list";
import PracticeTeamProvider, { usePracticeTeam } from "../../contexts/practice-team-context";
import destroyPracticeTeam from "../../apis/destroyPracticeTeam";
import { Toaster, Badge } from "@td/ui";
import { goToPracticeTeams, editPracticeTeamPath } from "../../utils/url-helper";
import { ActionsContainer, StatesContainer } from "../../components";
import moment from "moment";
import toastr from "toastr";

const PracticeTeamShow = () => {
  const { practiceTeam } = usePracticeTeam();

  const handleDestroy = useCallback(async () => {
    const isConfirmed = confirm(
      translate(null, "practice_team.deletion", "confirmation")
    );

    if (!isConfirmed) {
      return;
    }

    const { errors } = await destroyPracticeTeam(practiceTeam.globalId);

    if (errors.length === 0) {
      toastr.success(
        translate(null, "practice_team.deletion", "notification")
      );

      goToPracticeTeams();
    } else {
      toastr.error(
        translate(null, "practice_team", "server_error")
      );
    }
  });

  return (
    <div className="section">
      <h2 data-testid="practice-team-show-header">
        <I18n scope="practice_team" text="team_info" />
      </h2>

      <ActionsContainer>
        <a
          className="edit"
          href={editPracticeTeamPath(practiceTeam.id)}
          data-testid="practice-team-show-edit-action"
        >
          {translate(null, "practice_team.actions", "edit")}
        </a>
        <a
          className="delete"
          onClick={handleDestroy}
          data-testid="practice-team-show-delete-action"
        >
          {translate(null, "practice_team.actions", "delete")}
        </a>
      </ActionsContainer>

      <table className="fields">
        <colgroup>
          <col style={{ width: 200 }} />
          <col style={{ width: 200 }} />
          <col style={{ width: 200 }} />
          <col style={{ width: "auto" }} />
        </colgroup>
        <tbody>
          <tr>
            <th data-testid="practice-team-name-label">
              {translate(null, "practice_team.fields", "name")}:
            </th>
            <td data-testid="practice-team-name">
              {practiceTeam.name}
            </td>
            <th data-testid="practice-team-states-label">
              {translate(null, "practice_team.fields.states", "label")}:
            </th>
            <td
              rowSpan={3}
              style={{ verticalAlign: "top" }}
              data-testid="practice-team-states"
            >
              {practiceTeam.states.length > 0 ? (
                <StatesContainer>
                  {practiceTeam.states.map((state, index) => (
                    <Badge key={index}>
                      {state}
                    </Badge>
                  ))}
                </StatesContainer>
              ) : (
                <React.Fragment>
                  {translate(null, "practice_team.fields.states.messages", "empty")}
                </React.Fragment>
              )}
            </td>
          </tr>
          <tr>
            <th data-testid="practice-team-created-at-label">
              {translate(null, "practice_team.fields", "created_at")}:
            </th>
            <td data-testid="practice-team-created-at">
              {moment(practiceTeam.createdAt).format("L")}
            </td>
          </tr>
          <tr>
            <th data-testid="practice-team-updated-at-label">
              {translate(null, "practice_team.fields", "updated_at")}:
            </th>
            <td data-testid="practice-team-updated-at">
              {practiceTeam.updatedAt !== practiceTeam.createdAt && (
                <React.Fragment>
                  {moment(practiceTeam.updatedAt).format("L")}
                </React.Fragment>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <ParticipantsList
        participants={practiceTeam.practiceTeamParticipants}
      />
    </div>
  );
};

const PracticeTeamShowContainer = (props) => (
  <PracticeTeamProvider {...props}>
    <PracticeTeamShow {...props}/>
    <Toaster />
  </PracticeTeamProvider>
);

PracticeTeamShowContainer.displayName = "PracticeTeamShowContainer";

export default PracticeTeamShowContainer;
